import { ApprovedVideo, FolderLayout } from './approved-content.types';

/** Pure rules for the Approved Content tab: filtering, counts, sorting, selection, names. */

export type StatusFilter = 'all' | 'new';
export type DeliverableFilter = 'all' | number;
export type SortKey = 'approved' | 'creator' | 'video' | 'size';
export type CheckState = 'on' | 'mixed' | 'off';

export interface ListFilters {
  status: StatusFilter;
  deliverable: DeliverableFilter;
  query: string;
  sort: SortKey;
}

/** "Rosie K." — first name and last initial, falling back to the full name. */
export function shortCreatorName(creator: ApprovedVideo['creator']): string {
  const first = creator.firstName?.trim();
  if (!first) return creator.name;
  const initial = creator.lastName?.trim()[0];
  return initial ? `${first} ${initial.toUpperCase()}.` : first;
}

export function deliverableName(index: number): string {
  return `Video ${index + 1}`;
}

/**
 * Second line under "Video 1": the brief piece's label, else the uploaded file's name. A label
 * that only repeats the number ("Video 2") adds nothing, so it falls back to the file name.
 */
export function deliverableSub(video: ApprovedVideo): string {
  const label = video.deliverableLabel?.trim();
  return label && !/^video\s*\d+$/i.test(label) ? label : video.videoName;
}

function matchesQuery(video: ApprovedVideo, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [video.creator.name, deliverableName(video.deliverableIndex), video.deliverableLabel ?? '', video.videoName]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

function matchesStatus(video: ApprovedVideo, status: StatusFilter): boolean {
  return status === 'all' || !video.downloadedByMe;
}

function matchesDeliverable(video: ApprovedVideo, deliverable: DeliverableFilter): boolean {
  return deliverable === 'all' || video.deliverableIndex === deliverable;
}

const SORTS: Record<SortKey, (a: ApprovedVideo, b: ApprovedVideo) => number> = {
  approved: (a, b) => b.approvedAt.localeCompare(a.approvedAt) || a.id.localeCompare(b.id),
  creator: (a, b) => a.creator.name.localeCompare(b.creator.name) || a.deliverableIndex - b.deliverableIndex,
  video: (a, b) => a.deliverableIndex - b.deliverableIndex || b.approvedAt.localeCompare(a.approvedAt),
  size: (a, b) => (b.fileSizeBytes ?? 0) - (a.fileSizeBytes ?? 0),
};

/** The table rows: both filters and the search combine (AND), then the chosen sort. */
export function visibleVideos(videos: readonly ApprovedVideo[], f: ListFilters): ApprovedVideo[] {
  return videos
    .filter((v) => matchesStatus(v, f.status) && matchesDeliverable(v, f.deliverable) && matchesQuery(v, f.query))
    .sort(SORTS[f.sort]);
}

/** Download-status counts reflect the deliverable filter and search, not the status filter. */
export function statusCounts(
  videos: readonly ApprovedVideo[],
  deliverable: DeliverableFilter,
  query: string,
): { all: number; new: number } {
  const base = videos.filter((v) => matchesDeliverable(v, deliverable) && matchesQuery(v, query));
  return { all: base.length, new: base.filter((v) => !v.downloadedByMe).length };
}

/** Deliverable counts reflect the status filter and search, not the deliverable filter. */
export function deliverableCounts(
  videos: readonly ApprovedVideo[],
  status: StatusFilter,
  query: string,
  indexes: readonly number[],
): { all: number; byIndex: Map<number, number> } {
  const base = videos.filter((v) => matchesStatus(v, status) && matchesQuery(v, query));
  const byIndex = new Map(indexes.map((i) => [i, base.filter((v) => v.deliverableIndex === i).length]));
  return { all: base.length, byIndex };
}

/** Header checkbox: toggling selects all visible rows, or clears them if all were selected. */
export function toggleIds(selected: ReadonlySet<string>, ids: readonly string[]): Set<string> {
  const next = new Set(selected);
  const allOn = ids.length > 0 && ids.every((id) => next.has(id));
  for (const id of ids) {
    if (allOn) next.delete(id);
    else next.add(id);
  }
  return next;
}

export function checkState(selected: ReadonlySet<string>, ids: readonly string[]): CheckState {
  const on = ids.filter((id) => selected.has(id)).length;
  if (ids.length > 0 && on === ids.length) return 'on';
  return on > 0 ? 'mixed' : 'off';
}

/** The previewed video: the current one if still visible, else the first visible row. */
export function resolveCurrent(visible: readonly ApprovedVideo[], currentId: string | null): string | null {
  if (currentId && visible.some((v) => v.id === currentId)) return currentId;
  return visible[0]?.id ?? null;
}

export function stepCurrent(visible: readonly ApprovedVideo[], currentId: string | null, delta: 1 | -1): string | null {
  const i = visible.findIndex((v) => v.id === currentId);
  return visible[i + delta]?.id ?? null;
}

// ----- formatting ----------------------------------------------------------------------

/** "86 MB" / "1.2 GB" (decimal units, like the OS file browser). */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '–';
  const mb = bytes / 1_000_000;
  if (mb >= 1000) return `${(mb / 1000).toFixed(mb >= 10_000 ? 0 : 1).replace(/\.0$/, '')} GB`;
  if (mb >= 1) return `${Math.round(mb)} MB`;
  return `${Math.max(1, Math.round(bytes / 1000))} KB`;
}

export function totalBytes(videos: readonly ApprovedVideo[]): number {
  return videos.reduce((sum, v) => sum + (v.fileSizeBytes ?? 0), 0);
}

/** "0:28", "1:05". */
export function formatDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** "Today", "Yesterday", or "Mon 26 Oct" (adds the year when it isn't this year). */
export function formatApprovedDate(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((day(now) - day(date)) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
  const rest = date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
  return `${weekday} ${rest}`;
}

// ----- downloads (mirror the API's naming in approved-content.helpers.ts) ---------------

export function fileSafe(text: string | null | undefined, fallback = 'Untitled'): string {
  const words = (text ?? '')
    .normalize('NFKD')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return words.map((w) => w[0].toUpperCase() + w.slice(1)).join('') || fallback;
}

export function videoFileStem(video: ApprovedVideo): string {
  const parts = [fileSafe(video.creator.name, 'Creator'), `Video${video.deliverableIndex + 1}`];
  if (video.deliverableLabel) parts.push(fileSafe(video.deliverableLabel));
  return parts.join('_');
}

export function downloadRoot(brandName: string, briefTitle: string): string {
  return [fileSafe(brandName, 'Brand'), fileSafe(briefTitle, 'Brief')].join('_');
}

/** The path shown under "Organise folders" for one example video. */
export function examplePath(layout: FolderLayout, root: string, video: ApprovedVideo): string {
  const file = `${videoFileStem(video)}.mp4`;
  if (layout === 'creator') return `${root}/${fileSafe(video.creator.name, 'Creator')}/${file}`;
  if (layout === 'video') return `${root}/Video${video.deliverableIndex + 1}/${file}`;
  return `${root}/${file}`;
}

export interface DownloadPlan {
  kind: 'mp4' | 'zip';
  bytes: number;
  tooLarge: boolean;
}

/** One video with nothing extra is a plain MP4; anything else is a ZIP. */
export function downloadPlan(
  videos: readonly ApprovedVideo[],
  includeSheet: boolean,
  sheetBytes: number,
  maxBytes: number,
): DownloadPlan {
  const bytes = totalBytes(videos) + (includeSheet ? sheetBytes : 0);
  const kind = videos.length === 1 && !includeSheet ? 'mp4' : 'zip';
  return { kind, bytes, tooLarge: bytes > maxBytes };
}

export function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] || fullName;
}
